import axios from 'axios';

import apiClient from '../client';
import { putToSignedUrl } from './putToSignedUrl';
import { ModificationApiError } from './modificationApi';
import type { ModificationApi, ModificationApiErrorKind } from './modificationApi';

// The live AI-modification client: the gateway's routes E1 to E15 through the
// shared apiClient, which sends the Firebase token and the session cookie
// (api/client.ts). Idempotency-Key goes on E3, E5, E11 and E12 only, the four
// writes the gateway makes replayable. Ids are encoded into the path.
//
// Error bodies are never trusted: only a string message from
// {"error":{"message":"..."}} is kept, and only for a 422 (it names the field
// the server refused) or a 409 (the gateway's own fixed sentence, such as the
// upload link limit, which the upload screen shows as sent).

const KIND_BY_STATUS: Readonly<Record<number, ModificationApiErrorKind>> = Object.freeze({
  401: 'auth',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
  422: 'invalid',
  503: 'unavailable',
});

const messageIn = (data: unknown): string | undefined => {
  if (data && typeof data === 'object') {
    const error = (data as { error?: unknown }).error;
    if (error && typeof error === 'object') {
      const message = (error as { message?: unknown }).message;
      return typeof message === 'string' ? message : undefined;
    }
  }
  return undefined;
};

const normalizeError = (e: unknown): ModificationApiError => {
  if (e instanceof ModificationApiError) return e;
  const err = e as { isAxiosError?: boolean; response?: { status?: number; data?: unknown } };
  if (axios.isAxiosError(e) || err?.isAxiosError) {
    if (!err.response) {
      return new ModificationApiError('No response from the gateway.', { kind: 'network' });
    }
    const status = err.response.status ?? 0;
    const kind = KIND_BY_STATUS[status] ?? 'unknown';
    return new ModificationApiError(`The gateway answered ${status}.`, {
      kind,
      status,
      serverMessage: kind === 'invalid' || kind === 'conflict' ? messageIn(err.response.data) : undefined,
    });
  }
  return new ModificationApiError('The request failed.', { kind: 'unknown' });
};

const call = async <T>(request: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const response = await request();
    return response.data;
  } catch (e) {
    throw normalizeError(e);
  }
};

const id = (value: string) => encodeURIComponent(value);
const withKey = (idempotencyKey: string) => ({ headers: { 'Idempotency-Key': idempotencyKey } });

/** Only the filters actually given; an empty filter is no filter. */
const params = (query: Record<string, string | boolean | undefined>) =>
  Object.fromEntries(
    Object.entries(query)
      .filter(([, value]) => value !== undefined && value !== '' && value !== false)
      .map(([key, value]) => [key, String(value)])
  );

// Every PDF starts with these five characters. An answer that does not is not
// the contract, whatever its status said (contractService.ts does the same).
const PDF_START = '%PDF-';
const startsLikeAPdf = (bytes: Uint8Array): boolean =>
  bytes.length >= PDF_START.length && Array.from(PDF_START).every((character, index) => bytes[index] === character.charCodeAt(0));

const headerOf = (headers: unknown, name: string): string | null => {
  if (!headers || typeof headers !== 'object') return null;
  const value = (headers as Record<string, unknown>)[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

export const liveModificationApiClient: ModificationApi = {
  getTerms: (signal) => call(() => apiClient.get('/modification-terms/current', { signal })),

  getEligibility: (orderId, signal) => call(() => apiClient.get(`/modifications/eligibility/${id(orderId)}`, { signal })),

  previewAgreement: (body) =>
    call(() =>
      apiClient.post('/modifications/agreement-preview', {
        orderId: body.orderId,
        modificationTypeId: body.modificationTypeId,
        instructions: body.instructions,
        production: body.production,
      })
    ),

  // The agreement, only when the addendum is on: the preview the buyer agreed to, nothing else.
  createModification: (orderId, idempotencyKey, agreement) =>
    call(() => apiClient.post('/modifications', agreement ? { orderId, agreement } : { orderId }, withKey(idempotencyKey))),

  issueUploadSlot: (modificationId, file) =>
    call(() =>
      apiClient.post(`/modifications/${id(modificationId)}/revisions`, {
        fileName: file.fileName,
        contentType: file.contentType,
        sizeBytes: file.sizeBytes,
      })
    ),

  uploadFile: async (slot, file, onProgress, signal) => {
    try {
      await putToSignedUrl(slot.uploadUrl, file, slot.requiredHeaders, onProgress, signal);
    } catch (e) {
      throw normalizeError(e);
    }
  },

  submitRevision: (modificationId, revision, body, idempotencyKey) =>
    call(() => apiClient.post(`/modifications/${id(modificationId)}/revisions/${revision}/submit`, body, withKey(idempotencyKey))),

  listMine: (query, signal) => call(() => apiClient.get('/modifications', { params: params(query), signal })),

  getModification: (modificationId, signal) => call(() => apiClient.get(`/modifications/${id(modificationId)}`, { signal })),

  getRevisionFile: (modificationId, revision) => call(() => apiClient.get(`/modifications/${id(modificationId)}/revisions/${revision}/file`)),

  getContract: async (modificationId, signal) => {
    let response: { data: ArrayBuffer; headers?: unknown };
    try {
      response = await apiClient.get(`/modifications/${id(modificationId)}/contract`, { responseType: 'arraybuffer', signal });
    } catch (e) {
      throw normalizeError(e);
    }
    const bytes = new Uint8Array(response.data);
    if (!startsLikeAPdf(bytes)) {
      throw new ModificationApiError('The contract answer is not a PDF.', { kind: 'unknown' });
    }
    return {
      file: new Blob([bytes], { type: 'application/pdf' }),
      sha256: headerOf(response.headers, 'x-content-sha256'),
      orderStatus: headerOf(response.headers, 'x-order-status'),
    };
  },

  getAcceptancePdf: async (modificationId, acceptanceId, signal) => {
    let response: { data: ArrayBuffer; headers?: unknown };
    try {
      response = await apiClient.get(`/modifications/${id(modificationId)}/acceptances/${id(acceptanceId)}/pdf`, { responseType: 'arraybuffer', signal });
    } catch (e) {
      throw normalizeError(e);
    }
    const bytes = new Uint8Array(response.data);
    if (!startsLikeAPdf(bytes)) {
      throw new ModificationApiError('The agreement answer is not a PDF.', { kind: 'unknown' });
    }
    // The file name is the server's own (Content-Disposition: attachment; filename="...").
    const disposition = headerOf(response.headers, 'content-disposition');
    const named = disposition ? /filename="([^"]+)"/.exec(disposition) : null;
    return { file: new Blob([bytes], { type: 'application/pdf' }), sha256: headerOf(response.headers, 'x-content-sha256'), filename: named ? named[1] : null };
  },

  listForLicensor: (query, signal) => call(() => apiClient.get('/licensor/modifications', { params: params(query), signal })),

  decide: (modificationId, body, idempotencyKey) =>
    call(() => apiClient.post(`/licensor/modifications/${id(modificationId)}/decision`, body, withKey(idempotencyKey))),

  resetLock: (modificationId, body, idempotencyKey) =>
    call(() => apiClient.post(`/admin/modifications/${id(modificationId)}/lock-reset`, body, withKey(idempotencyKey))),

  listForAdmin: (query, signal) => call(() => apiClient.get('/admin/modifications', { params: params(query), signal })),
};
